import { Button, Form, Space, Typography } from 'antd'
import { simpleSuccessNotification } from '../../../../../../../hooks/notificationHandler'
import {
  useProjectsForRefreshRedisCacheQuery,
  useRefreshRedisCacheMutation,
} from '../../../../../../../schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from '../../../../../../../utils/defaults'
import { useForm, useWatch } from 'antd/lib/form/Form'
import { alphabeticalCompare } from '@cims-online/shared'
import CIMSSelect from '../../../../../../../components/CIMSSelect'
import CIMSFormItem from '../../../../../../../components/CIMSFormItem'
import { useOptions } from '../../../../../../../components/CIMSSelect/hooks/useOptions'
import { useState } from 'react'
import { SharePointResourceFormItemsValues } from './components/SharePointResourceFormItems/types'
import SharePointResourceFormItems from './components/SharePointResourceFormItems'
import UserFormItems from './components/UserFormItems'
import { UserFormItemsValues } from './components/UserFormItems/types'

const Redis: React.VFC = () => {
  const [refresh] = useRefreshRedisCacheMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({ refreshRedisCache: { removed } }) =>
      simpleSuccessNotification(`Removed ${removed} keys from Redis`),
  })
  const [refreshing, setRefreshing] = useState(false)
  const { data, loading, refetch } = useProjectsForRefreshRedisCacheQuery(
    DefaultQueryHookOptions
  )
  const projectOptions = useOptions({
    items: data?.projects?.records,
    sort: (a, b) =>
      alphabeticalCompare(
        a.projectName ?? a.globalId,
        b.projectName ?? b.globalId
      ),
    label: ({ projectName, globalId }) => projectName ?? globalId,
    value: 'globalId',
  })

  const [form] = useForm<
    | ({ hashKey: 'users' } & UserFormItemsValues)
    | ({
        hashKey: 'sharePointResources' | 'packetSharePointResources'
      } & SharePointResourceFormItemsValues)
  >()
  const hashKey = useWatch('hashKey', form)
  return (
    <Space direction="vertical" style={{ width: '75%' }}>
      <Typography.Paragraph>
        At times it becomes necessary to manually clear different caches or keys
        in Redis.
      </Typography.Paragraph>
      <Typography.Paragraph>
        Select a Hash Key, and other available modifiers, to clear different
        aspects of the cache.
      </Typography.Paragraph>
      <Form
        form={form}
        onFinish={async (values) => {
          setRefreshing(true)
          await refresh({ variables: { input: values } })
          setRefreshing(false)
        }}
      >
        <CIMSFormItem
          name="hashKey"
          requiredMessage="Please select a Hash Key"
          label="Hash Key"
        >
          <CIMSSelect
            simple
            options={[
              { label: 'Users', value: 'users' },
              { label: 'SharePoint Resources', value: 'sharePointResources' },
              {
                label: 'Packet SharePoint Resources',
                value: 'packetSharePointResources',
              },
            ]}
          />
        </CIMSFormItem>
        {hashKey === 'users' && <UserFormItems />}
        {(hashKey === 'sharePointResources' ||
          hashKey === 'packetSharePointResources') && (
          <SharePointResourceFormItems
            loading={loading}
            refetch={refetch}
            projectOptions={projectOptions}
          />
        )}
        <Button
          type="primary"
          htmlType="submit"
          loading={refreshing}
          disabled={refreshing || loading}
        >
          Refresh
        </Button>
      </Form>
    </Space>
  )
}

export default Redis
